import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminListFilterDropdown,
  AdminMultiSelectFilterDropdown,
  AdminHeaderAction,
  AdminLinkAction,
  AdminMainText,
  AdminMetadataText,
  AdminMetric,
  AdminMetricDelta,
  AdminPageTitle,
  AdminPanelTitle,
  AdminRowActionsButton,
  AdminRowMenuIcon,
  AdminSearchField,
  AdminStatusBadge,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  AdminWidgetTitle,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';

import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import useClickOutside from '../../../Hooks/useClickOutside';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';

// LOCKED VALUES - Aligned with Queue & Archive
const CREDENTIAL_CATEGORY_OPTIONS = ['Licence', 'Ticket', 'Badge'];
const VERIFICATION_MODE_OPTIONS = ['Manual', 'Auto', 'Mixed'];
const AUTO_REMOVE_TRIGGER_OPTIONS = ['On Expiry', 'On Document Removal', 'Manual'];
const STATUS_OPTIONS = ['Active', 'Suspended', 'Retired'];

const buildDummyRules = () => {
  return [
    {
      id: 'RULE-LIC-PL-VIC',
      ruleId: 'RULE-LIC-PL-VIC',
      name: 'Licensed Plumber (VIC)',
      category: 'Licence',
      criteriaSummary: 'Valid Plumbing Licence + Active ABN',
      verificationMode: 'Auto',
      autoRemoveTrigger: 'On Expiry',
      linkedDocuments: ['Plumbing Licence (VIC)', 'ABN'],
      status: 'Active',
    },
    {
      id: 'RULE-TICKET-WH',
      ruleId: 'RULE-TICKET-WH',
      name: 'Working at Heights',
      category: 'Ticket',
      criteriaSummary: 'Valid work-at-heights ticket',
      verificationMode: 'Auto',
      autoRemoveTrigger: 'On Expiry',
      linkedDocuments: ['WAH Ticket'],
      status: 'Active',
    },
    {
      id: 'RULE-BADGE-ID-VER',
      ruleId: 'RULE-BADGE-ID-VER',
      name: 'Identity Verified',
      category: 'Badge',
      criteriaSummary: 'Valid Photo ID + cross-check',
      verificationMode: 'Manual',
      autoRemoveTrigger: 'Manual',
      linkedDocuments: ['ID Document'],
      status: 'Active',
    },
    {
      id: 'RULE-LIC-EL-NSW',
      ruleId: 'RULE-LIC-EL-NSW',
      name: 'Licensed Electrician (NSW)',
      category: 'Licence',
      criteriaSummary: 'Valid Electrical Licence + Active ABN',
      verificationMode: 'Auto',
      autoRemoveTrigger: 'On Expiry',
      linkedDocuments: ['Electrical Licence (NSW)', 'ABN'],
      status: 'Active',
    },
    {
      id: 'RULE-LIC-BU-QLD',
      ruleId: 'RULE-LIC-BU-QLD',
      name: 'Licensed Builder (QLD)',
      category: 'Licence',
      criteriaSummary: 'Valid Builder Licence + Active ABN',
      verificationMode: 'Mixed',
      autoRemoveTrigger: 'On Expiry',
      linkedDocuments: ['Builder Licence (QLD)', 'ABN'],
      status: 'Active',
    },
    {
      id: 'RULE-TICKET-FA',
      ruleId: 'RULE-TICKET-FA',
      name: 'First Aid Certified',
      category: 'Ticket',
      criteriaSummary: 'Valid First Aid Certificate',
      verificationMode: 'Auto',
      autoRemoveTrigger: 'On Expiry',
      linkedDocuments: ['First Aid Certificate'],
      status: 'Active',
    },
    {
      id: 'RULE-BADGE-INS-PL',
      ruleId: 'RULE-BADGE-INS-PL',
      name: 'Public Liability Insurance',
      category: 'Badge',
      criteriaSummary: 'Public liability insurance current',
      verificationMode: 'Auto',
      autoRemoveTrigger: 'On Expiry',
      linkedDocuments: ['Insurance Certificate'],
      status: 'Active',
    },
    {
      id: 'RULE-TICKET-WC',
      ruleId: 'RULE-TICKET-WC',
      name: 'White Card',
      category: 'Ticket',
      criteriaSummary: 'Valid White Card',
      verificationMode: 'Auto',
      autoRemoveTrigger: 'On Document Removal',
      linkedDocuments: ['White Card'],
      status: 'Suspended',
    },
    {
      id: 'RULE-LIC-PL-OLD',
      ruleId: 'RULE-LIC-PL-OLD',
      name: 'Licensed Plumber (Legacy)',
      category: 'Licence',
      criteriaSummary: 'Valid Plumbing Licence (Legacy Rule)',
      verificationMode: 'Manual',
      autoRemoveTrigger: 'Manual',
      linkedDocuments: ['Plumbing Licence'],
      status: 'Retired',
    },
  ];
};

const ComplianceRulebook = () => {
  const navigate = useNavigate();

  const [rules] = useState(buildDummyRules);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    credentialCategory: [],
    verificationMode: null,
    autoRemoveTrigger: null,
    status: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('ruleId');
  const [sortOrder, setSortOrder] = useState('asc');
  const [rowActionsOpen, setRowActionsOpen] = useState({});
  const rowActionsRefs = useRef({});

  // Refs for dropdowns
  const credentialCategoryRef = useRef(null);
  const verificationModeRef = useRef(null);
  const autoRemoveTriggerRef = useRef(null);
  const statusRef = useRef(null);

  // Dropdown states
  const [credentialCategoryOpen, setCredentialCategoryOpen] = useState(false);
  const [verificationModeOpen, setVerificationModeOpen] = useState(false);
  const [autoRemoveTriggerOpen, setAutoRemoveTriggerOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);

  // Click outside handlers
  useClickOutside(credentialCategoryRef, () => setCredentialCategoryOpen(false));
  useClickOutside(verificationModeRef, () => setVerificationModeOpen(false));
  useClickOutside(autoRemoveTriggerRef, () => setAutoRemoveTriggerOpen(false));
  useClickOutside(statusRef, () => setStatusOpen(false));

  const filteredRules = useMemo(() => {
    const lowerSearch = search.trim().toLowerCase();
    return rules.filter((rule) => {
      // Search filter
      const matchesSearch =
        !lowerSearch ||
        rule.name.toLowerCase().includes(lowerSearch) ||
        rule.ruleId.toLowerCase().includes(lowerSearch) ||
        rule.criteriaSummary.toLowerCase().includes(lowerSearch) ||
        rule.linkedDocuments.some(doc => doc.toLowerCase().includes(lowerSearch));

      // Credential Category filter
      const matchesCategory =
        filters.credentialCategory.length === 0 || filters.credentialCategory.includes(rule.category);

      // Verification Mode filter
      const matchesVerificationMode =
        !filters.verificationMode || rule.verificationMode === filters.verificationMode;

      // Auto-Remove Trigger filter
      const matchesAutoRemoveTrigger =
        !filters.autoRemoveTrigger || rule.autoRemoveTrigger === filters.autoRemoveTrigger;

      // Status filter
      const matchesStatus =
        !filters.status || rule.status === filters.status;

      return matchesSearch && matchesCategory && matchesVerificationMode && matchesAutoRemoveTrigger && matchesStatus;
    });
  }, [rules, search, filters]);

  const sortedRules = useMemo(() => {
    const sorted = [...filteredRules].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'linkedDocuments') {
        aValue = a.linkedDocuments.join(', ');
        bValue = b.linkedDocuments.join(', ');
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    return sorted;
  }, [filteredRules, sortField, sortOrder]);

  const totalCount = sortedRules.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
  const effectivePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    setCurrentPage((p) => Math.min(p, Math.max(1, Math.ceil(sortedRules.length / CONFIG_LIST_PAGE_SIZE))));
  }, [sortedRules.length]);

  const paginatedRules = useMemo(() => {
    const start = (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE;
    return sortedRules.slice(start, start + CONFIG_LIST_PAGE_SIZE);
  }, [sortedRules, effectivePage]);

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'credentialCategory') {
      setFilters(prev => ({
        ...prev,
        credentialCategory: prev.credentialCategory.includes(value)
          ? prev.credentialCategory.filter(cat => cat !== value)
          : [...prev.credentialCategory, value],
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [filterType]: prev[filterType] === value ? null : value,
      }));
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return <i className={`fi fi-rs-arrow-${sortOrder === 'asc' ? 'up' : 'down'} text-xs`}></i>;
  };

  const toggleRowActions = (ruleId) => {
    setRowActionsOpen((prev) => {
      if (prev[ruleId]) return {};
      return { [ruleId]: true };
    });
  };

  const handleRowAction = (action, rule) => {
    setRowActionsOpen(prev => ({ ...prev, [rule.id]: false }));
    
    switch (action) {
      case 'view':
        // View Rule - read-only preview
        navigate(`/admin/verification/rules/${rule.id}`, { state: { viewMode: true } });
        break;
      case 'edit':
        // Edit Rule - with warning about forward-only changes
        if (window.confirm('Changes apply only to future verifications. Continue editing?')) {
          navigate(`/admin/verification/rules/${rule.id}`);
        }
        break;
      case 'suspend':
        if (window.confirm('Are you sure you want to suspend this rule? Status will change to Suspended.')) {
          // TODO: Implement suspend API call
          console.log('Suspend rule:', rule.ruleId);
        }
        break;
      case 'activate':
        if (window.confirm('Are you sure you want to activate this rule? Status will change to Active.')) {
          // TODO: Implement activate API call
          console.log('Activate rule:', rule.ruleId);
        }
        break;
      case 'duplicate':
        navigate(`/admin/verification/rules/new`, { state: { duplicateFrom: rule } });
        break;
      case 'retire':
        if (window.confirm('Are you sure you want to retire this rule? Status will change to Retired and it cannot be reactivated.')) {
          // TODO: Implement retire API call
          console.log('Retire rule:', rule.ruleId);
        }
        break;
      default:
        break;
    }
  };

  // Click outside handler for row actions
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(rowActionsRefs.current).forEach((ruleId) => {
        const ref = rowActionsRefs.current[ruleId];
        if (ref && !ref.contains(event.target)) {
          setRowActionsOpen(prev => ({ ...prev, [ruleId]: false }));
        }
      });
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const rangeStart = totalCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalCount);
  const showPagination = totalCount > CONFIG_LIST_PAGE_SIZE;

  return (
    <AdminLayout>
      <div>
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex-1">
              <AdminPageTitle>Compliance Rulebook</AdminPageTitle>
            </div>
            <AdminHeaderAction
              onClick={() => navigate('/admin/verification/rules/new', { state: {} })}
            >
              + Add rule
            </AdminHeaderAction>
          </div>

        {/* Search and Filters Row */}
        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            {/* Search - Left */}
            <div className="w-full max-w-[450px]">
              <AdminSearchField
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Filters - Right */}
            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminMultiSelectFilterDropdown
                label="Credential Category"
                emptyLabel="All Credential Categories"
                selected={filters.credentialCategory}
                options={CREDENTIAL_CATEGORY_OPTIONS}
                onToggle={(category) => handleFilterChange('credentialCategory', category)}
                menuRef={credentialCategoryRef}
                open={credentialCategoryOpen}
                setOpen={setCredentialCategoryOpen}
              />
              <AdminListFilterDropdown
                label="Verification Mode"
                value={filters.verificationMode}
                options={[
                  { value: null, label: 'All Verification Modes' },
                  ...VERIFICATION_MODE_OPTIONS.map((option) => ({ value: option, label: option })),
                ]}
                onChange={(value) => handleFilterChange('verificationMode', value)}
                menuRef={verificationModeRef}
                open={verificationModeOpen}
                setOpen={setVerificationModeOpen}
              />
              <AdminListFilterDropdown
                label="Auto-Remove Trigger"
                value={filters.autoRemoveTrigger}
                options={[
                  { value: null, label: 'All Auto-Remove Triggers' },
                  ...AUTO_REMOVE_TRIGGER_OPTIONS.map((option) => ({ value: option, label: option })),
                ]}
                onChange={(value) => handleFilterChange('autoRemoveTrigger', value)}
                menuRef={autoRemoveTriggerRef}
                open={autoRemoveTriggerOpen}
                setOpen={setAutoRemoveTriggerOpen}
              />
              <AdminListFilterDropdown
                label="Status"
                value={filters.status}
                options={[
                  { value: null, label: 'All Statuses' },
                  ...STATUS_OPTIONS.map((status) => ({ value: status, label: status })),
                ]}
                onChange={(value) => handleFilterChange('status', value)}
                menuRef={statusRef}
                open={statusOpen}
                setOpen={setStatusOpen}
              />
            </div>
          </div>
        </div>

        {/* Rules Table */}
        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          {/* Header */}
          <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(150px, 1.5fr) minmax(130px, 1.5fr) minmax(200px, 2fr) minmax(100px, 1fr) minmax(150px, 1.5fr) minmax(180px, 1.8fr) minmax(100px, 1fr) minmax(60px, 0.6fr)' }}>
            <div className="flex items-center">
              <span className="font-semibold">Rule ID</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Rule Name</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Credential Category</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Criteria Summary</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Verification Mode</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Auto-Remove Trigger</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Linked Documents</span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold">Status</span>
            </div>
            <div className="flex items-center justify-center">
              <AdminTableHeader>Actions</AdminTableHeader></div>
          </div>

          {/* Rows */}
          {paginatedRules.length === 0 ? (
            <div className="py-10 px-4 text-center">
              <AdminMainText className="text-mediumgray">
                No compliance rules found.
              </AdminMainText>
            </div>
          ) : (
            paginatedRules.map((rule) => (
              <div
                key={rule.id}
                className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4 cursor-pointer"
                style={{ gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(150px, 1.5fr) minmax(130px, 1.5fr) minmax(200px, 2fr) minmax(100px, 1fr) minmax(150px, 1.5fr) minmax(180px, 1.8fr) minmax(100px, 1fr) minmax(60px, 0.6fr)' }}
                onClick={() => navigate(`/admin/verification/rules/${rule.id}`, { state: { viewMode: true } })}
              >
                <div className="flex items-center min-w-0">
                  <span className="font-mono text-offblack truncate" title={rule.ruleId}>
                    {rule.ruleId}
                  </span>
                </div>
                <div className="flex items-center min-w-0">
                  <span className="text-offblack truncate" title={rule.name}>
                    {rule.name}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="text-offblack">{rule.category}</span>
                </div>
                <div className="flex items-center min-w-0">
                  <span className="text-offblack truncate" title={rule.criteriaSummary}>
                    {rule.criteriaSummary}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="text-offblack">{rule.verificationMode}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-offblack">{rule.autoRemoveTrigger}</span>
                </div>
                <div className="flex items-center min-w-0">
                  <span className="text-offblack truncate" title={rule.linkedDocuments.join(', ')}>
                    {rule.linkedDocuments.join(', ')}
                  </span>
                </div>
                <div className="flex items-center">
                  <AdminStatusBadge status={rule.status} label={rule.status} />
                </div>
                <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                  <div className="relative inline-flex" ref={el => rowActionsRefs.current[rule.id] = el}>
                    <AdminRowActionsButton
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleRowActions(rule.id);
                      }}
                      title="Row Actions"
                      aria-label="Row actions"
                    >
                      <AdminRowMenuIcon />
                    </AdminRowActionsButton>
                    {rowActionsOpen[rule.id] && (
                      <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[200px] shadow-lg border border-lightgray">
                        <div className="py-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowAction('view', rule);
                            }}
                            className="block px-3 py-1.5 hover:bg-gray-100 hover:text-primaryblue w-full text-left transition-colors"
                          >
                            <AdminSupportingText>View Rule</AdminSupportingText>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowAction('edit', rule);
                            }}
                            className="block px-3 py-1.5 hover:bg-gray-100 hover:text-primaryblue w-full text-left transition-colors"
                          >
                            <AdminSupportingText>Edit Rule</AdminSupportingText>
                          </button>
                          {rule.status === 'Active' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRowAction('suspend', rule);
                              }}
                              className="block px-3 py-1.5 hover:bg-gray-100 hover:text-primaryblue w-full text-left transition-colors"
                            >
                              <AdminSupportingText>Suspend Rule</AdminSupportingText>
                            </button>
                          )}
                          {rule.status === 'Suspended' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRowAction('activate', rule);
                              }}
                              className="block px-3 py-1.5 hover:bg-gray-100 hover:text-primaryblue w-full text-left transition-colors"
                            >
                              <AdminSupportingText>Activate Rule</AdminSupportingText>
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowAction('duplicate', rule);
                            }}
                            className="block px-3 py-1.5 hover:bg-gray-100 hover:text-primaryblue w-full text-left transition-colors"
                          >
                            <AdminSupportingText>Duplicate Rule</AdminSupportingText>
                          </button>
                          {rule.status !== 'Retired' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRowAction('retire', rule);
                              }}
                              className="block px-3 py-1.5 hover:bg-red-50 hover:text-alertred w-full text-left transition-colors"
                            >
                              <AdminSupportingText>Retire Rule</AdminSupportingText>
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={effectivePage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default ComplianceRulebook;

